import { useMemo, useState } from "react";
import { Figure, Corner, SectionHead } from "./Primitives";
import { Illu } from "./Illustrations";
import { ArrowRight, Lock } from "lucide-react";

const FILTERS = [
  { key: "all", label: "Alle" },
  { key: "dystopisch", label: "Dystopische Projekte" },
  { key: "wannado", label: "Andere Wanna-dos" },
];

const STATUS = {
  available: { label: "Verfügbar", cls: "text-status-available border-status-available" },
  reserved: { label: "Reserviert", cls: "text-status-reserved border-status-reserved" },
  taken: { label: "Vergeben", cls: "text-status-taken border-status-taken" },
};

export const Motifs = ({ texts, motifs, onRequest }) => {
  const [filter, setFilter] = useState("all");
  const list = useMemo(
    () => (filter === "all" ? motifs : motifs.filter((m) => m.category === filter)),
    [filter, motifs]
  );

  return (
    <section id="motive" data-testid="motifs-section" className="relative border-b border-paper-line bg-paper-surface/60 ap-section">
      <div className="ap-container">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead stamp="AP · VERFÜGBAR" title={texts.motifs_h2} intro={texts.motifs_intro} />
          <div className="ap-illu hidden h-28 w-28 shrink-0 lg:block" aria-hidden="true">
            <Illu name={texts.illu_motifs} fallback="portal" className="h-full w-full" />
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3" role="group" aria-label="Motive filtern">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              aria-pressed={filter === f.key}
              data-testid={`motif-filter-${f.key}`}
              className={`min-h-[44px] border px-5 font-mono text-[11px] uppercase tracking-[0.16em] transition-colors duration-200 ${
                filter === f.key
                  ? "border-signal bg-signal text-white"
                  : "border-paper-line bg-paper text-charcoal hover:border-ink hover:text-ink"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((m, i) => {
            const st = STATUS[m.status];
            return (
              <article
                key={m.id}
                data-testid={`motif-card-${i}`}
                className="ap-card group flex flex-col bg-paper animate-rise"
                style={{ animationDelay: `${i * 50}ms` }}
              >
                <Corner />
                <div className="relative">
                  <Figure
                    src={m.image}
                    mediaId={m.image_media_id}
                    alt={`${m.title} – verfügbares Tattoomotiv, ${m.placement}`}
                    code={m.code}
                    ratio="aspect-[4/5]"
                  />
                  <span
                    className={`absolute left-3 top-3 border bg-paper/95 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.16em] ${st.cls}`}
                  >
                    {st.label}
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-2 p-5">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-display text-lg font-bold uppercase tracking-wide text-ink">{m.title}</h3>
                    <span className="ap-stamp shrink-0">{m.code}</span>
                  </div>
                  <p className="ap-stamp">
                    {m.category === "dystopisch" ? "Dystopisches Projekt" : "Wanna-do"}
                  </p>
                  <p className="ap-body text-charcoal">{m.description}</p>
                  <dl className="mt-2 space-y-1 border-t border-paper-line pt-3 text-xs text-charcoal">
                    <div className="flex justify-between gap-2">
                      <dt className="ap-stamp">Körperstelle</dt>
                      <dd className="text-right">{m.placement || "—"}</dd>
                    </div>
                    {m.size ? (
                      <div className="flex justify-between gap-2">
                        <dt className="ap-stamp">Größe</dt>
                        <dd className="text-right">{m.size}</dd>
                      </div>
                    ) : null}
                  </dl>

                  <div className="mt-4">
                    {m.requestable && m.status !== "taken" ? (
                      <button
                        type="button"
                        onClick={() => onRequest(m)}
                        data-testid={`motif-request-${i}`}
                        className="ap-btn-solid w-full"
                      >
                        Dieses Motiv anfragen <ArrowRight size={15} />
                      </button>
                    ) : (
                      <p className="flex min-h-[48px] items-center justify-center gap-2 border border-paper-line bg-paper-subtle px-4 font-mono text-[11px] uppercase tracking-[0.16em] text-charcoal">
                        <Lock size={13} /> Nicht anfragbar
                      </p>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {list.length === 0 ? (
          <p className="mt-10 border border-paper-line bg-paper p-6 ap-body text-charcoal" data-testid="motifs-empty">
            In dieser Kategorie sind derzeit keine Motive verfügbar. Eigene Ideen sind jederzeit willkommen.
          </p>
        ) : null}
      </div>
    </section>
  );
};
